/**
 * CloudLens icon set — Phosphor, exposed under the Material icon names the app already
 * uses, with an adapter that keeps MUI's icon API working.
 *
 * The adapter is the point. Phosphor icons take size/weight/color; the 40 call sites here
 * use MUI's conventions instead — sx (99 uses), fontSize="small"|"large"|"inherit" (58),
 * and palette words like color="success" (12). Swapping the import alone would have
 * rendered every icon at the wrong size and dropped its semantic colour, so muiIcon()
 * translates all three:
 *
 *   - fontSize keyword -> MUI's exact SvgIcon rem sizes, so footprints are unchanged.
 *     Works because Phosphor icons default to size="1em".
 *   - palette word     -> theme path (success -> success.main), resolved by sx.
 *     Works because Phosphor icons default to color="currentColor".
 *   - sx               -> applied last so call-site overrides still win.
 *
 * Names stay Material (AddIcon, not PlusIcon): renaming 239 bindings would be a large
 * diff for no user-visible gain, and the alias documents each icon's intent.
 *
 * dist/csr, not dist/ssr: CloudLens is a client-rendered CRA app, and only './dist/csr/*'
 * is wildcarded in the package's exports map, which webpack enforces.
 *
 * Mirrors lib/phosphor-icons.ts in the main CloudForge app so both products share one
 * icon language.
 */
import React from 'react';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';

import { ArrowClockwise } from '@phosphor-icons/react/dist/csr/ArrowClockwise';
import { ArrowLeft } from '@phosphor-icons/react/dist/csr/ArrowLeft';
import { ArrowRight } from '@phosphor-icons/react/dist/csr/ArrowRight';
import { ArrowSquareOut } from '@phosphor-icons/react/dist/csr/ArrowSquareOut';
import { ArrowsClockwise } from '@phosphor-icons/react/dist/csr/ArrowsClockwise';
import { Brain } from '@phosphor-icons/react/dist/csr/Brain';
import { Bug } from '@phosphor-icons/react/dist/csr/Bug';
import { Buildings } from '@phosphor-icons/react/dist/csr/Buildings';
import { CalendarBlank } from '@phosphor-icons/react/dist/csr/CalendarBlank';
import { CaretDown } from '@phosphor-icons/react/dist/csr/CaretDown';
import { CaretUp } from '@phosphor-icons/react/dist/csr/CaretUp';
import { ChartBar } from '@phosphor-icons/react/dist/csr/ChartBar';
import { ChartLine } from '@phosphor-icons/react/dist/csr/ChartLine';
import { ChartPie } from '@phosphor-icons/react/dist/csr/ChartPie';
import { ChatCircle } from '@phosphor-icons/react/dist/csr/ChatCircle';
import { Check } from '@phosphor-icons/react/dist/csr/Check';
import { CheckCircle } from '@phosphor-icons/react/dist/csr/CheckCircle';
import { Circle } from '@phosphor-icons/react/dist/csr/Circle';
import { ClipboardText } from '@phosphor-icons/react/dist/csr/ClipboardText';
import { Cloud } from '@phosphor-icons/react/dist/csr/Cloud';
import { CloudArrowUp } from '@phosphor-icons/react/dist/csr/CloudArrowUp';
import { CloudCheck } from '@phosphor-icons/react/dist/csr/CloudCheck';
import { CloudSlash } from '@phosphor-icons/react/dist/csr/CloudSlash';
import { Code } from '@phosphor-icons/react/dist/csr/Code';
import { Copy } from '@phosphor-icons/react/dist/csr/Copy';
import { CurrencyCircleDollar } from '@phosphor-icons/react/dist/csr/CurrencyCircleDollar';
import { CurrencyDollar } from '@phosphor-icons/react/dist/csr/CurrencyDollar';
import { Database } from '@phosphor-icons/react/dist/csr/Database';
import { DownloadSimple } from '@phosphor-icons/react/dist/csr/DownloadSimple';
import { EscalatorUp } from '@phosphor-icons/react/dist/csr/EscalatorUp';
import { Eye } from '@phosphor-icons/react/dist/csr/Eye';
import { EyeSlash } from '@phosphor-icons/react/dist/csr/EyeSlash';
import { FloppyDisk } from '@phosphor-icons/react/dist/csr/FloppyDisk';
import { FunnelSimple } from '@phosphor-icons/react/dist/csr/FunnelSimple';
import { Gear } from '@phosphor-icons/react/dist/csr/Gear';
import { Globe } from '@phosphor-icons/react/dist/csr/Globe';
import { House } from '@phosphor-icons/react/dist/csr/House';
import { Info } from '@phosphor-icons/react/dist/csr/Info';
import { Key } from '@phosphor-icons/react/dist/csr/Key';
import { Lightbulb } from '@phosphor-icons/react/dist/csr/Lightbulb';
import { Lightning } from '@phosphor-icons/react/dist/csr/Lightning';
import { Link } from '@phosphor-icons/react/dist/csr/Link';
import { ListChecks } from '@phosphor-icons/react/dist/csr/ListChecks';
import { Lock } from '@phosphor-icons/react/dist/csr/Lock';
import { LockKeyOpen } from '@phosphor-icons/react/dist/csr/LockKeyOpen';
import { MagicWand } from '@phosphor-icons/react/dist/csr/MagicWand';
import { MagnifyingGlass } from '@phosphor-icons/react/dist/csr/MagnifyingGlass';
import { MinusCircle } from '@phosphor-icons/react/dist/csr/MinusCircle';
import { Network } from '@phosphor-icons/react/dist/csr/Network';
import { PaperPlaneTilt } from '@phosphor-icons/react/dist/csr/PaperPlaneTilt';
import { Paperclip } from '@phosphor-icons/react/dist/csr/Paperclip';
import { PencilSimple } from '@phosphor-icons/react/dist/csr/PencilSimple';
import { PiggyBank } from '@phosphor-icons/react/dist/csr/PiggyBank';
import { Play } from '@phosphor-icons/react/dist/csr/Play';
import { Plus } from '@phosphor-icons/react/dist/csr/Plus';
import { Pulse } from '@phosphor-icons/react/dist/csr/Pulse';
import { Question } from '@phosphor-icons/react/dist/csr/Question';
import { Robot } from '@phosphor-icons/react/dist/csr/Robot';
import { ShareNetwork } from '@phosphor-icons/react/dist/csr/ShareNetwork';
import { Shield } from '@phosphor-icons/react/dist/csr/Shield';
import { ShieldCheck } from '@phosphor-icons/react/dist/csr/ShieldCheck';
import { ShieldPlus } from '@phosphor-icons/react/dist/csr/ShieldPlus';
import { SignOut } from '@phosphor-icons/react/dist/csr/SignOut';
import { Sparkle } from '@phosphor-icons/react/dist/csr/Sparkle';
import { Speedometer } from '@phosphor-icons/react/dist/csr/Speedometer';
import { SquaresFour } from '@phosphor-icons/react/dist/csr/SquaresFour';
import { StopCircle } from '@phosphor-icons/react/dist/csr/StopCircle';
import { Tag } from '@phosphor-icons/react/dist/csr/Tag';
import { Ticket } from '@phosphor-icons/react/dist/csr/Ticket';
import { Trash } from '@phosphor-icons/react/dist/csr/Trash';
import { TrashSimple } from '@phosphor-icons/react/dist/csr/TrashSimple';
import { TrendDown } from '@phosphor-icons/react/dist/csr/TrendDown';
import { TrendUp } from '@phosphor-icons/react/dist/csr/TrendUp';
import { User } from '@phosphor-icons/react/dist/csr/User';
import { UserGear } from '@phosphor-icons/react/dist/csr/UserGear';
import { Wallet } from '@phosphor-icons/react/dist/csr/Wallet';
import { Warning } from '@phosphor-icons/react/dist/csr/Warning';
import { WarningCircle } from '@phosphor-icons/react/dist/csr/WarningCircle';
import { WifiHigh } from '@phosphor-icons/react/dist/csr/WifiHigh';
import { Wrench } from '@phosphor-icons/react/dist/csr/Wrench';
import { X } from '@phosphor-icons/react/dist/csr/X';
import { XCircle } from '@phosphor-icons/react/dist/csr/XCircle';

type MuiFontSize = 'inherit' | 'small' | 'medium' | 'large';

/** MUI SvgIcon's own sizes, so migrated icons occupy exactly the space they used to. */
const FONT_SIZE: Record<MuiFontSize, string> = {
  inherit: 'inherit',
  small: '1.25rem',
  medium: '1.5rem',
  large: '2.1875rem',
};

/** MUI palette shorthands -> theme paths that sx can resolve. */
const PALETTE: Record<string, string> = {
  inherit: 'inherit',
  action: 'action.active',
  disabled: 'action.disabled',
  primary: 'primary.main',
  secondary: 'secondary.main',
  error: 'error.main',
  info: 'info.main',
  success: 'success.main',
  warning: 'warning.main',
};

export interface MuiIconProps {
  sx?: SxProps<Theme>;
  fontSize?: MuiFontSize;
  /** A MUI palette word, or any CSS colour. */
  color?: string;
  className?: string;
  titleAccess?: string;
  onClick?: React.MouseEventHandler<SVGSVGElement>;
  [key: string]: unknown;
}

function muiIcon(Ic: React.ComponentType<any>, name: string) {
  const Wrapped = ({ sx, fontSize = 'medium', color, titleAccess, ...rest }: MuiIconProps) => (
    <Box
      component={Ic as any}
      aria-label={titleAccess}
      aria-hidden={titleAccess ? undefined : true}
      sx={{
        fontSize: FONT_SIZE[fontSize as MuiFontSize] ?? fontSize,
        flexShrink: 0,
        ...(color ? { color: PALETTE[color] ?? color } : null),
        ...sx,
      }}
      {...rest}
    />
  );
  Wrapped.displayName = name;
  return Wrapped;
}

export const AccountBalanceWalletIcon = muiIcon(Wallet, 'AccountBalanceWalletIcon');
export const AddIcon = muiIcon(Plus, 'AddIcon');
export const ArrowBackIcon = muiIcon(ArrowLeft, 'ArrowBackIcon');
export const ArrowForwardIcon = muiIcon(ArrowRight, 'ArrowForwardIcon');
export const AssessmentIcon = muiIcon(ChartBar, 'AssessmentIcon');
export const AssignmentIcon = muiIcon(ClipboardText, 'AssignmentIcon');
export const AttachFileIcon = muiIcon(Paperclip, 'AttachFileIcon');
export const AttachMoneyIcon = muiIcon(CurrencyDollar, 'AttachMoneyIcon');
export const AutoAwesomeIcon = muiIcon(Sparkle, 'AutoAwesomeIcon');
export const AutoFixHighIcon = muiIcon(MagicWand, 'AutoFixHighIcon');
export const BackupIcon = muiIcon(CloudArrowUp, 'BackupIcon');
export const BugReportIcon = muiIcon(Bug, 'BugReportIcon');
export const BuildIcon = muiIcon(Wrench, 'BuildIcon');
export const BusinessIcon = muiIcon(Buildings, 'BusinessIcon');
export const CalendarMonthIcon = muiIcon(CalendarBlank, 'CalendarMonthIcon');
export const CancelIcon = muiIcon(XCircle, 'CancelIcon');
export const ChangeCircleIcon = muiIcon(ArrowsClockwise, 'ChangeCircleIcon');
export const ChatBubbleOutlineIcon = muiIcon(ChatCircle, 'ChatBubbleOutlineIcon');
export const CheckIcon = muiIcon(Check, 'CheckIcon');
export const CheckCircleIcon = muiIcon(CheckCircle, 'CheckCircleIcon');
export const CheckCircleOutlineIcon = muiIcon(CheckCircle, 'CheckCircleOutlineIcon');
export const ChecklistIcon = muiIcon(ListChecks, 'ChecklistIcon');
export const ClearIcon = muiIcon(X, 'ClearIcon');
export const CloseIcon = muiIcon(X, 'CloseIcon');
export const CloudIcon = muiIcon(Cloud, 'CloudIcon');
export const CloudDoneIcon = muiIcon(CloudCheck, 'CloudDoneIcon');
export const CloudOffIcon = muiIcon(CloudSlash, 'CloudOffIcon');
export const CloudOutlinedIcon = muiIcon(Cloud, 'CloudOutlinedIcon');
export const CodeIcon = muiIcon(Code, 'CodeIcon');
export const ConfirmationNumberIcon = muiIcon(Ticket, 'ConfirmationNumberIcon');
export const ContentCopyIcon = muiIcon(Copy, 'ContentCopyIcon');
export const DashboardIcon = muiIcon(SquaresFour, 'DashboardIcon');
export const DeleteIcon = muiIcon(Trash, 'DeleteIcon');
export const DeleteOutlineIcon = muiIcon(Trash, 'DeleteOutlineIcon');
export const DeleteSweepIcon = muiIcon(TrashSimple, 'DeleteSweepIcon');
export const DownloadIcon = muiIcon(DownloadSimple, 'DownloadIcon');
export const EditIcon = muiIcon(PencilSimple, 'EditIcon');
export const EditOutlinedIcon = muiIcon(PencilSimple, 'EditOutlinedIcon');
export const ErrorIcon = muiIcon(WarningCircle, 'ErrorIcon');
export const ErrorOutlineIcon = muiIcon(WarningCircle, 'ErrorOutlineIcon');
export const EscalatorIcon = muiIcon(EscalatorUp, 'EscalatorIcon');
export const ExpandLessIcon = muiIcon(CaretUp, 'ExpandLessIcon');
export const ExpandMoreIcon = muiIcon(CaretDown, 'ExpandMoreIcon');
export const FilterListIcon = muiIcon(FunnelSimple, 'FilterListIcon');
export const FlashOnIcon = muiIcon(Lightning, 'FlashOnIcon');
export const HealthAndSafetyIcon = muiIcon(ShieldPlus, 'HealthAndSafetyIcon');
export const HelpOutlineIcon = muiIcon(Question, 'HelpOutlineIcon');
export const HomeIcon = muiIcon(House, 'HomeIcon');
export const InfoIcon = muiIcon(Info, 'InfoIcon');
export const InfoOutlinedIcon = muiIcon(Info, 'InfoOutlinedIcon');
export const KeyIcon = muiIcon(Key, 'KeyIcon');
export const LabelIcon = muiIcon(Tag, 'LabelIcon');
export const LanIcon = muiIcon(ShareNetwork, 'LanIcon');
export const LightbulbIcon = muiIcon(Lightbulb, 'LightbulbIcon');
export const LinkIcon = muiIcon(Link, 'LinkIcon');
export const LocalOfferIcon = muiIcon(Tag, 'LocalOfferIcon');
export const LockIcon = muiIcon(Lock, 'LockIcon');
export const LockOutlinedIcon = muiIcon(Lock, 'LockOutlinedIcon');
export const LockResetOutlinedIcon = muiIcon(LockKeyOpen, 'LockResetOutlinedIcon');
export const LogoutIcon = muiIcon(SignOut, 'LogoutIcon');
export const ManageAccountsIcon = muiIcon(UserGear, 'ManageAccountsIcon');
export const MonetizationOnOutlinedIcon = muiIcon(CurrencyCircleDollar, 'MonetizationOnOutlinedIcon');
export const MonitorHeartIcon = muiIcon(Pulse, 'MonitorHeartIcon');
export const NetworkCheckIcon = muiIcon(WifiHigh, 'NetworkCheckIcon');
export const OpenInNewIcon = muiIcon(ArrowSquareOut, 'OpenInNewIcon');
export const PersonIcon = muiIcon(User, 'PersonIcon');
export const PersonOutlineIcon = muiIcon(User, 'PersonOutlineIcon');
export const PieChartIcon = muiIcon(ChartPie, 'PieChartIcon');
export const PlayArrowIcon = muiIcon(Play, 'PlayArrowIcon');
export const PsychologyIcon = muiIcon(Brain, 'PsychologyIcon');
export const PublicIcon = muiIcon(Globe, 'PublicIcon');
export const RadioButtonUncheckedIcon = muiIcon(Circle, 'RadioButtonUncheckedIcon');
export const RefreshIcon = muiIcon(ArrowClockwise, 'RefreshIcon');
export const RemoveCircleIcon = muiIcon(MinusCircle, 'RemoveCircleIcon');
export const RouterIcon = muiIcon(Network, 'RouterIcon');
export const SaveIcon = muiIcon(FloppyDisk, 'SaveIcon');
export const SavingsIcon = muiIcon(PiggyBank, 'SavingsIcon');
export const SearchIcon = muiIcon(MagnifyingGlass, 'SearchIcon');
export const SecurityIcon = muiIcon(ShieldCheck, 'SecurityIcon');
export const SecurityOutlinedIcon = muiIcon(ShieldCheck, 'SecurityOutlinedIcon');
export const SendIcon = muiIcon(PaperPlaneTilt, 'SendIcon');
export const SettingsIcon = muiIcon(Gear, 'SettingsIcon');
export const ShieldIcon = muiIcon(Shield, 'ShieldIcon');
export const ShieldOutlinedIcon = muiIcon(Shield, 'ShieldOutlinedIcon');
export const SmartToyIcon = muiIcon(Robot, 'SmartToyIcon');
export const SpeedIcon = muiIcon(Speedometer, 'SpeedIcon');
export const StopCircleOutlinedIcon = muiIcon(StopCircle, 'StopCircleOutlinedIcon');
export const StorageIcon = muiIcon(Database, 'StorageIcon');
export const SyncIcon = muiIcon(ArrowsClockwise, 'SyncIcon');
export const TimelineIcon = muiIcon(ChartLine, 'TimelineIcon');
export const TipsAndUpdatesIcon = muiIcon(Lightbulb, 'TipsAndUpdatesIcon');
export const TrendingDownIcon = muiIcon(TrendDown, 'TrendingDownIcon');
export const TrendingUpIcon = muiIcon(TrendUp, 'TrendingUpIcon');
export const VerifiedUserIcon = muiIcon(ShieldCheck, 'VerifiedUserIcon');
export const VisibilityIcon = muiIcon(Eye, 'VisibilityIcon');
export const VisibilityOffIcon = muiIcon(EyeSlash, 'VisibilityOffIcon');
export const VpnKeyIcon = muiIcon(Key, 'VpnKeyIcon');
export const WarningIcon = muiIcon(Warning, 'WarningIcon');
export const WarningAmberIcon = muiIcon(Warning, 'WarningAmberIcon');
